import type {
  Bubble,
  ComicPage,
  EditorState,
  Panel,
  PanelPlacement,
  SaveStatus,
  Selection,
  StudioBootstrap,
} from "./types";

const HISTORY_LIMIT = 50;

export interface EditorModel {
  studio: StudioBootstrap;
  page: ComicPage;
  state: EditorState;
  selection: Selection;
  past: EditorState[];
  future: EditorState[];
  saveStatus: SaveStatus;
  message: string;
  preview: boolean;
}

export type EditorAction =
  | { type: "select"; selection: Selection }
  | { type: "commit"; state: EditorState }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "save-start" }
  | { type: "save-success"; revision: number; keepDirty: boolean }
  | { type: "save-failure"; conflict: boolean; message: string }
  | { type: "retry-save" }
  | { type: "message"; message: string }
  | { type: "preview"; value: boolean }
  | { type: "replace-panel"; panel: Panel }
  | { type: "panel-image"; panel: Panel }
  | { type: "panel-status"; panel: Panel }
  | { type: "panel-content-saved"; panel: Panel }
  | { type: "refresh-studio"; studio: StudioBootstrap }
  | { type: "production-refresh"; studio: StudioBootstrap; page: ComicPage; replaceEditor: boolean }
  | { type: "replace-studio"; studio: StudioBootstrap };

function copyState(state: EditorState): EditorState {
  return structuredClone(state);
}

export function initialEditorModel(studio: StudioBootstrap): EditorModel | null {
  if (!studio.active_page) {
    return null;
  }
  return {
    studio,
    page: studio.active_page,
    state: copyState(studio.active_page.editor_state),
    selection: { kind: "page" },
    past: [],
    future: [],
    saveStatus: "idle",
    message: "",
    preview: false,
  };
}

export function editorReducer(model: EditorModel, action: EditorAction): EditorModel {
  switch (action.type) {
    case "select":
      return { ...model, selection: action.selection, message: "" };
    case "commit":
      return {
        ...model,
        past: [...model.past.slice(-(HISTORY_LIMIT - 1)), copyState(model.state)],
        state: copyState(action.state),
        future: [],
        saveStatus: "dirty",
        message: "",
      };
    case "undo": {
      const previous = model.past.at(-1);
      if (!previous) {
        return model;
      }
      return {
        ...model,
        state: copyState(previous),
        past: model.past.slice(0, -1),
        future: [copyState(model.state), ...model.future].slice(0, HISTORY_LIMIT),
        saveStatus: "dirty",
      };
    }
    case "redo": {
      const next = model.future[0];
      if (!next) {
        return model;
      }
      return {
        ...model,
        state: copyState(next),
        past: [...model.past, copyState(model.state)].slice(-HISTORY_LIMIT),
        future: model.future.slice(1),
        saveStatus: "dirty",
      };
    }
    case "save-start":
      return { ...model, saveStatus: "saving", message: "" };
    case "save-success":
      return {
        ...model,
        page: {
          ...model.page,
          editor_state: action.keepDirty
            ? model.page.editor_state
            : copyState(model.state),
          revision: action.revision,
        },
        saveStatus: action.keepDirty ? "dirty" : "saved",
      };
    case "save-failure":
      return {
        ...model,
        saveStatus: action.conflict ? "conflict" : "error",
        message: action.message,
      };
    case "retry-save":
      return model.saveStatus === "error" ? { ...model, saveStatus: "dirty" } : model;
    case "message":
      return { ...model, message: action.message };
    case "preview":
      return { ...model, preview: action.value, selection: { kind: "page" } };
    case "panel-content-saved":
      return { ...model, page:{ ...model.page, panels:model.page.panels.map((panel) => panel.id === action.panel.id ? {...panel,content:action.panel.content} : panel) } };
    case "panel-status":
      return { ...model, page:{ ...model.page, panels:model.page.panels.map((panel) => panel.id === action.panel.id ? {...panel,generation_status:action.panel.generation_status} : panel) } };
    case "panel-image": {
      const panels = model.page.panels.map((panel) => panel.id === action.panel.id
        ? { ...panel, current_asset_id: action.panel.current_asset_id,
            current_asset_url: action.panel.current_asset_url, generation_status: action.panel.generation_status }
        : panel);
      return { ...model, page: { ...model.page, panels } };
    }
    case "refresh-studio": {
      if (model.studio.active_project?.id !== action.studio.active_project?.id) return model;
      let next = model;
      if (action.studio.active_page?.id === model.page.id) {
        for (const panel of action.studio.active_page.panels) next = editorReducer(next, { type: "panel-status", panel });
      }
      return { ...next, studio: { ...action.studio, active_page: next.page } };
    }
    case "production-refresh": {
      if (model.studio.active_project?.id !== action.studio.active_project?.id) return model;
      if (action.replaceEditor) {
        if (action.page.id !== model.page.id) {
          return initialEditorModel({ ...action.studio, active_page: action.page }) ?? model;
        }
        return {
          ...model,
          studio: { ...action.studio, active_page: action.page },
          page: action.page,
          state: copyState(action.page.editor_state),
          saveStatus: "saved",
          message: "",
        };
      }
      if (action.page.id !== model.page.id) {
        return { ...model, studio: { ...action.studio, active_page: model.page } };
      }
      const refreshedPanels = model.page.panels.map((panel) => {
        const refreshed = action.page.panels.find((item) => item.id === panel.id);
        return refreshed
          ? {
              ...panel,
              current_asset_id: refreshed.current_asset_id,
              current_asset_url: refreshed.current_asset_url,
              generation_status: refreshed.generation_status,
            }
          : panel;
      });
      const refreshedPage = { ...model.page, panels: refreshedPanels };
      return {
        ...model,
        studio: { ...action.studio, active_page: refreshedPage },
        page: refreshedPage,
      };
    }
    case "replace-panel": {
      const panels = model.page.panels.map((panel) =>
        panel.id === action.panel.id ? action.panel : panel,
      );
      return { ...model, page: { ...model.page, panels }, message: "" };
    }
    case "replace-studio": {
      const next = initialEditorModel(action.studio);
      return next ?? model;
    }
  }
}

export function updateBubble(
  state: EditorState,
  bubbleId: string,
  updates: Partial<Bubble>,
): EditorState {
  return {
    ...state,
    bubbles: state.bubbles.map((bubble) =>
      bubble.id === bubbleId ? { ...bubble, ...updates } : bubble,
    ),
  };
}

export function updatePage(
  state: EditorState,
  updates: Partial<EditorState["page"]>,
): EditorState {
  return { ...state, page: { ...state.page, ...updates } };
}

export function updatePanelPlacement(
  state: EditorState,
  panelId: string,
  updates: Partial<PanelPlacement>,
): EditorState {
  return {
    ...state,
    panel_placements: state.panel_placements.map((placement) =>
      placement.panel_id === panelId ? { ...placement, ...updates } : placement,
    ),
  };
}

export function newBubble(panelId: string | null, outputLanguage = "ja"): Bubble {
  const isJapanese = outputLanguage === "ja";
  return {
    id: crypto.randomUUID(),
    panel_id: panelId,
    source_line_id: "",
    source_character_id: null,
    source_kind: "dialogue",
    speaker_name: "",
    reading_order: 0,
    type: "speech",
    text: isJapanese ? "セリフ" : "",
    writing_mode: isJapanese ? "vertical-rl" : "horizontal-tb",
    x: 0.66,
    y: 0.08,
    width: 0.14,
    height: isJapanese ? 0.16 : 0.1,
    rotation: 0,
    z_index: 20,
    style: {
      font_family: "Noto Sans JP",
      font_size: 28,
      text_color: "#111111",
      background_color: "#ffffff",
      border_color: "#111111",
      border_width: 3,
      tail_direction: "bottom-left",
    },
  };
}

export function bubbleSizeForText(
  kind: Bubble["source_kind"],
  text: string,
  placement: PanelPlacement,
): Pick<Bubble, "width" | "height"> {
  const length = Array.from(text.trim()).length;
  if (kind === "sound_effects") {
    if (length <= 10) {
      return {
        width: Math.min(0.105, placement.width * 0.24),
        height: Math.min(0.065, placement.height * 0.18),
      };
    }
    if (length <= 20) {
      return {
        width: Math.min(0.135, placement.width * 0.3),
        height: Math.min(0.08, placement.height * 0.22),
      };
    }
    return {
      width: Math.min(0.16, placement.width * 0.36),
      height: Math.min(0.1, placement.height * 0.26),
    };
  }
  if (kind === "narration") {
    if (length <= 28) {
      return {
        width: Math.min(0.18, placement.width * 0.42),
        height: Math.min(0.085, placement.height * 0.24),
      };
    }
    if (length <= 56) {
      return {
        width: Math.min(0.2, placement.width * 0.48),
        height: Math.min(0.12, placement.height * 0.33),
      };
    }
    return {
      width: Math.min(0.22, placement.width * 0.55),
      height: Math.min(0.14, placement.height * 0.36),
    };
  }
  if (length <= 24) {
    return {
      width: Math.min(0.16, placement.width * 0.38),
      height: Math.min(0.115, placement.height * 0.3),
    };
  }
  if (length <= 50) {
    return {
      width: Math.min(0.175, placement.width * 0.42),
      height: Math.min(0.14, placement.height * 0.36),
    };
  }
  return {
    width: Math.min(0.205, placement.width * 0.5),
    height: Math.min(0.18, placement.height * 0.46),
  };
}


export function panelWithBubbleText(panel: Panel, state: EditorState): Panel {
  const content = structuredClone(panel.content);
  for (const bubble of state.bubbles) {
    if (bubble.panel_id !== panel.id || !bubble.source_line_id) continue;
    const kind = bubble.source_kind;
    const line = content[kind].find((item) => item.id === bubble.source_line_id);
    if (line) { line.text = bubble.text; line.character_id = bubble.source_character_id ?? null; }
    else content[kind].push({ id: bubble.source_line_id, text: bubble.text, character_id: bubble.source_character_id ?? null });
  }
  return { ...panel, content };
}

export function initialBubbleFontSize(bubble: Bubble, page: EditorState["page"]): number {
  if (bubble.type === "sound_effect") return bubble.style.font_size;
  // Keep the initial server/client pass aligned with the rendered page surface.
  const width = bubble.width * (page.width - 2 * (page.margin ?? 0)) - 24;
  const height = bubble.height * (page.height - 2 * (page.margin ?? 0)) - 24;
  const vertical = bubble.writing_mode === "vertical-rl";
  for (let size = bubble.style.font_size; size >= 8; size -= 1) {
    const perLine = Math.max(1, Math.floor((vertical ? height : width) / (size * 1.05)));
    const lines = bubble.text.split("\n").reduce((sum, line) =>
      sum + Math.max(1, Math.ceil((Array.from(line).length + 1) / perLine)), 0);
    if (lines * size * 1.35 <= (vertical ? width : height)) return size;
  }
  return 8;
}

export function syncPanelBubbles(state: EditorState, panel: Panel, characters: EditorModel["studio"]["characters"]): EditorState {
  return { ...state, bubbles: state.bubbles.flatMap((bubble) => {
    if (bubble.panel_id !== panel.id || !bubble.source_line_id) return [bubble];
    const line = panel.content[bubble.source_kind].find((item) => item.id === bubble.source_line_id);
    if (!line) return [];
    return [{ ...bubble, text: line.text, source_character_id: line.character_id,
      speaker_name: characters.find((item) => item.id === line.character_id)?.name ?? "" }];
  }) };
}

export function placePanelDialogue(model: EditorModel, panel: Panel): EditorState {
  const state = syncPanelBubbles(model.state, panel, model.studio.characters);
  const placement = state.panel_placements.find((item) => item.panel_id === panel.id);
  if (!placement) return state;
  const rtl = model.studio.project_data?.reading_direction === "rtl";
  const columnOffsets = [0, 0];
  const gap = Math.min(0.012, placement.height * 0.04);
  let flowOffset = 0;
  let index = 0;
  for (const kind of ["dialogue", "narration", "sound_effects"] as const) {
    for (const line of panel.content[kind]) {
      if (!line.text.trim()) continue;
      const existing = state.bubbles.find((bubble) => bubble.panel_id === panel.id && bubble.source_line_id === line.id && bubble.source_kind === kind);
      const column = index % 2;
      if (existing) {
        const existingEnd = Math.max(0, existing.y - placement.y) + existing.height + gap;
        columnOffsets[column] = Math.max(columnOffsets[column], existingEnd);
        flowOffset = Math.max(flowOffset, existingEnd);
        index += 1;
        continue;
      }
      const bubble = newBubble(panel.id, model.studio.project_data?.output_language ?? "ja");
      const { width, height } = bubbleSizeForText(kind, line.text, placement);
      const y = Math.min(
        placement.y + placement.height - height,
        placement.y + Math.max(flowOffset, columnOffsets[column]),
      );
      const placed: Bubble = { ...bubble, text: line.text, source_line_id: line.id, source_kind: kind,
        source_character_id: line.character_id, speaker_name: model.studio.characters.find((item) => item.id === line.character_id)?.name ?? "",
        reading_order: index, type: kind === "narration" ? "narration" : kind === "sound_effects" ? "sound_effect" : "speech",
        width, height, x: placement.x + (rtl ? 1 - column : column) * (placement.width - width),
        y,
      };
      placed.style.font_size = initialBubbleFontSize(placed, state.page);
      state.bubbles.push(placed);
      const end = y - placement.y + height + gap;
      columnOffsets[column] = Math.max(columnOffsets[column], end);
      flowOffset = Math.max(flowOffset, end);
      index += 1;
    }
  }
  return state;
}
